import { useMemo } from 'react'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import {
  shareHierarchyNode,
  getHierarchyNodeShares,
} from '@/api/hierarchyShares'
import type { HierarchyShareRead } from '@/api/hierarchyShares'
import type { UserSummary } from '@/api/shares'
import { queryKeys } from '@/lib/queryKeys'
import type { AxiosError } from 'axios'

interface ShareDepartmentsParams {
  departmentIds: string[]
  userIds: string[]
}

interface ShareDepartmentsResult {
  succeeded: number
  failed: { departmentId: string; error: string }[]
}

export function useShareDepartments() {
  const queryClient = useQueryClient()

  return useMutation<ShareDepartmentsResult, Error, ShareDepartmentsParams>({
    mutationFn: async ({ departmentIds, userIds }) => {
      const results = await Promise.allSettled(
        departmentIds.map(deptId =>
          shareHierarchyNode(deptId, { user_ids: userIds })
        )
      )

      const failed: ShareDepartmentsResult['failed'] = []
      let succeeded = 0

      results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          succeeded++
        } else {
          const axiosError = result.reason as AxiosError<{
            detail?: string
            title?: string
          }>
          const message =
            axiosError.response?.data?.detail ??
            axiosError.response?.data?.title ??
            axiosError.message ??
            'Unknown error'
          failed.push({ departmentId: departmentIds[index], error: message })
        }
      })

      if (failed.length === departmentIds.length) {
        throw new Error(failed[0].error)
      }

      return { succeeded, failed }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.hierarchyShares.all,
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.browse.all,
      })
    },
  })
}

export interface DepartmentSharesWithUsers {
  departmentId: string
  departmentName: string
  shares: Array<{ share: HierarchyShareRead; user: UserSummary | null }>
}

export function useDepartmentSharesWithUsers(
  departmentIds: string[],
  departmentNames: string[]
) {
  const sharesQueries = useQueries({
    queries: departmentIds.map(deptId => ({
      queryKey: queryKeys.hierarchyShares.byNodeId(deptId),
      queryFn: () => getHierarchyNodeShares(deptId),
      enabled: !!deptId,
    })),
  })

  const isLoading = sharesQueries.some(q => q.isLoading)

  const data: DepartmentSharesWithUsers[] = useMemo(() => {
    if (isLoading) return []

    return departmentIds.map((deptId, index) => {
      const queryResult = sharesQueries[index]
      const shares = queryResult?.data?.data ?? []

      return {
        departmentId: deptId,
        departmentName: departmentNames[index] ?? deptId,
        shares: shares.map(share => ({
          share,
          user: share.user,
        })),
      }
    })
  }, [departmentIds, departmentNames, sharesQueries, isLoading])

  return { data, isLoading }
}
